import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { CSSProperties, FC, ReactNode } from 'react';
import { CSSTransition } from 'react-transition-group';

import { TextHighlighter } from 'APP/components/TextHighlighter/TextHighlighter';

import { HIGHLIGHT_IN_AND_OUT_ANIMATION_DURATION } from '../../Message.constants';
import { useQuotedSelectionHighlightPresenter } from './QuotedSelectionHighlight.presenter';
import styles from './QuotedSelectionHighlight.styles.m.css';

interface IQuotedSelectionHighlightProps {
  className?: string;
  highlightedText?: string;
  originalText?: string;
  groupId?: string;
  children: ReactNode;
  isView?: boolean;
}

export const QuotedSelectionHighlight: FC<IQuotedSelectionHighlightProps> = observer((props) => {
  const { highlightedText, originalText, isView, ...restProps } = props;
  const { presenter, highlightRef, rootRef } = useQuotedSelectionHighlightPresenter({
    highlightedText,
    originalText,
    isView,
  });

  const CSSTransitionClasses = {
    enter: styles.highlightEnter,
    enterActive: styles.highlightEnterActive,
    exit: styles.highlightExit,
    exitActive: styles.highlightExitActive,
  };

  const containerClassName = classNames(styles.container, restProps.className);

  const containerStyle = {
    '--highlight-in-and-out-animation-duration': `${HIGHLIGHT_IN_AND_OUT_ANIMATION_DURATION}ms`,
  } as CSSProperties;

  return (
    <div
      style={containerStyle}
      className={containerClassName}
      ref={rootRef}
    >
      <div className={styles.wrapper}>
        <CSSTransition
          in={presenter.showHighlight}
          mountOnEnter
          unmountOnExit
          timeout={HIGHLIGHT_IN_AND_OUT_ANIMATION_DURATION}
          classNames={CSSTransitionClasses}
          onExited={presenter.unsetHighlightedText}
        >
          <TextHighlighter
            highlightRef={highlightRef}
            className={styles.textHighlighter}
            highlightClassName={styles.highlight}
            highlightedText={presenter.highlightedText}
            originalText={presenter.originalText || undefined}
            parentRef={rootRef}
          />
        </CSSTransition>
        <div className={styles.content}>{restProps.children}</div>
      </div>
    </div>
  );
});
